import { cn } from './cn';

const GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-violet-500 to-purple-600',
  'from-cyan-500 to-blue-600',
  'from-emerald-500 to-teal-600',
  'from-rose-500 to-pink-600',
  'from-amber-500 to-orange-600',
  'from-indigo-500 to-blue-700',
  'from-fuchsia-500 to-violet-600',
] as const;

function getGradient(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return GRADIENTS[Math.abs(hash) % GRADIENTS.length] ?? GRADIENTS[0];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0] ?? '';
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] ?? '';
  return ((first[0] ?? '') + (last[0] ?? '')).toUpperCase();
}

interface AvatarProps {
  name: string;
  src?: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZE_CLASSES: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  xs: 'h-6 w-6 text-[10px]',
  sm: 'h-8 w-8 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-11 w-11 text-base',
};

export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  const shouldUseImage =
    !!src &&
    !src.includes('pps.whatsapp.net') &&
    !src.includes('mmg.whatsapp.net') &&
    !src.includes('whatsapp.net');

  if (shouldUseImage) {
    return (
      <img
        src={src}
        alt={name}
        className={cn('shrink-0 rounded-full object-cover', SIZE_CLASSES[size], className)}
        onError={(e) => {
          // Fallback to initials on broken image
          const el = e.currentTarget as HTMLImageElement;
          el.style.display = 'none';
          if (el.nextSibling) (el.nextSibling as HTMLElement).style.display = 'flex';
        }}
      />
    );
  }

  const gradient = getGradient(name);
  const initials = getInitials(name);

  return (
    <div
      className={cn(
        'flex shrink-0 items-center justify-center rounded-full',
        'bg-linear-to-br font-semibold text-white',
        gradient,
        SIZE_CLASSES[size],
        className,
      )}
    >
      {initials}
    </div>
  );
}
